import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Container, Typography, Grid } from "@material-ui/core";

// components
import { useSelector } from "react-redux";
import { empleadosApi } from "src/services/empleadosApi";
import AppWidgetsFacturacionRecaudacionPendientes from "src/components/informes/App-widget-facturacion-recaudacion-pendientes";
import AppGraficoDeBarras from "src/components/informes/App-grafico-barras";
import { informesApi } from "src/services/informesApi";
import AppFacturasSucursales from "src/components/informes/App-facturas-sucursales";
import AppCrecimientoClientes from "src/components/informes/App-crecimiento-clientes";
import ModalDetalleFacturas from "src/components/Modal/modalDetalleFacturas";
import { facturasApi } from "src/services/facturasApi";
import {
  convertirFechaAFormatoNumerico,
  fechaActual,
  fechaDesde,
} from "src/utils/formatTime";
import { sucursalesApi } from "src/services/sucursalesApi";
import SucursalFilter from "src/components/estadisticas/sucursal-filter";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import {
  mapearSucursalesALabels,
  obtenerLabelSucursal,
} from "src/utils/sucursal-utils";
import AnioFilter from "src/components/estadisticas/anio-filter";

const getStorageKey = (rut) => ({
  sucursales: `informes_sucursales_${rut}`,
  anio: `informes_anio_${rut}`,
});

const crearBodySolicitud = (rut, filtro, fecha, sucursales) => ({
  rut,
  estadoFactura: filtro,
  fecha,
  sucursales,
});

export default function Informes() {
  const {
    empleado: user,
    sucursal: { refSucursal: sucursalEmpleado },
  } = useSelector((state) => state.auth.user);

  // Obtener sucursales del empleado
  const { data: sucursalesEmpleado = [] } =
    sucursalesApi.useGetSucursalesEmpleadosQuery(user.rut);

  // Storage keys
  const STORAGE_KEYS = useMemo(() => getStorageKey(user.rut), [user.rut]);

  // Función para cargar desde storage
  const cargarDesdeStorage = useCallback(() => {
    try {
      const sucursalesGuardadas = localStorage.getItem(STORAGE_KEYS.sucursales);
      const anioGuardado = localStorage.getItem(STORAGE_KEYS.anio);

      return {
        sucursales: sucursalesGuardadas
          ? JSON.parse(sucursalesGuardadas)
          : [sucursalEmpleado],
        anio: anioGuardado
          ? new Date(parseInt(anioGuardado), 0, 1)
          : new Date(),
      };
    } catch (error) {
      console.warn("Error al cargar preferencias:", error);
      return {
        sucursales: [sucursalEmpleado],
        anio: new Date(),
      };
    }
  }, [STORAGE_KEYS.sucursales, STORAGE_KEYS.anio, sucursalEmpleado]);

  const preferenciasGuardadas = cargarDesdeStorage();

  // Estado para filtros
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState(
    preferenciasGuardadas.sucursales,
  );

  const [anioSeleccionado, setAnioSeleccionado] = useState(
    preferenciasGuardadas.anio,
  );
  const obtenerSucursalesParaFiltro = useCallback(() => {
    if (sucursalesSeleccionadas.length > 0) {
      return sucursalesSeleccionadas;
    }
    if (sucursalesEmpleado && sucursalesEmpleado.length > 0) {
      return sucursalesEmpleado.map((s) => s.refSucursal);
    }
    return [];
  }, [sucursalesSeleccionadas, sucursalesEmpleado]);

  const [
    abrirModalEstadisticasDeFacturacion,
    setAbrirModalEstadisticasDeFacturacion,
  ] = useState(false);
  const [
    tituloModalEstadisticasFacturacion,
    setTituloModalEstadisticasFacturacion,
  ] = useState("");

  const {
    data: datosFacturacionAnio = [],
    isFetching: cargandoDatosFacturacionAnio,
  } = informesApi.useGetInformacionFacturacionAnioQuery(
    {
      rut: user.rut,
      sucursales: obtenerSucursalesParaFiltro(),
      anio: fechaDesde(anioSeleccionado).year(),
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const {
    data: datosEstadisticasFacturacion = [],
    isFetching: cargandoDatosEstadisticasFacturacion,
  } = informesApi.useGetInformacionEstadisticasFacturacionQuery(
    {
      rut: user.rut,
      sucursales: obtenerSucursalesParaFiltro(),
      anio: fechaDesde(anioSeleccionado).year(),
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: datosFacturacionPorSucursales = [],
    isFetching: cargandoDatosFacturacionPorSucursales,
  } = informesApi.useGetInformacionfacturacionPorSucursalesQuery(
    {
      rut: user.rut,
      sucursales: obtenerSucursalesParaFiltro(),
      anio: fechaDesde(anioSeleccionado).year(),
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: datosCrecimientoClientes = [],
    isFetching: cargandoDatosCrecimientoClientes,
  } = informesApi.useGetInformacionCrecimientoClientesQuery(
    {
      rut: user.rut,
      sucursales: obtenerSucursalesParaFiltro(),
      anio: fechaDesde(anioSeleccionado).year(),
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    obtenerFacturasEmitidas,
    { data: facturasEmitidas, isFetching: obteniendoFacturasEmitidas },
  ] = facturasApi.useLazyObtenerFacturasEmitidasQuery();

  const [addHistorialAcciones] = empleadosApi.useAddHistorialAccionesMutation();

  const sucursalesActualesRef = useRef();

  useEffect(() => {
    sucursalesActualesRef.current = obtenerSucursalesParaFiltro();
  }, [obtenerSucursalesParaFiltro]);

  const guardarSucursalesEnStorage = useCallback(
    (sucursales) => {
      try {
        localStorage.setItem(
          STORAGE_KEYS.sucursales,
          JSON.stringify(sucursales),
        );
      } catch (error) {
        console.warn("Error al guardar sucursales:", error);
      }
    },
    [STORAGE_KEYS.sucursales],
  );

  const handleSetSucursalesSeleccionadas = useCallback(
    (sucursales) => {
      setSucursalesSeleccionadas(sucursales);
      guardarSucursalesEnStorage(sucursales);
    },
    [guardarSucursalesEnStorage],
  );

  // Función para guardar año en storage
  const guardarAnioEnStorage = useCallback(
    (fecha) => {
      try {
        const anio = fechaActual(fecha).year();
        localStorage.setItem(STORAGE_KEYS.anio, anio.toString());
      } catch (error) {
        console.warn("Error al guardar año:", error);
      }
    },
    [STORAGE_KEYS.anio],
  );

  // Handler para cambio de año
  const handleSetAnioSeleccionado = useCallback(
    (fecha) => {
      setAnioSeleccionado(fecha);
      guardarAnioEnStorage(fecha);
    },
    [guardarAnioEnStorage],
  );

  useEffect(() => {
    let urlActual = window.location.href;
    if (urlActual.includes("http://localhost:3000")) {
    } else {
      addHistorialAcciones({
        user: user.rut,
        body: {
          urlAccion: urlActual,
        },
      })
        .unwrap()
        .then(() => {})
        .catch(() => {});
    }
  }, [addHistorialAcciones, user.rut]);

  const abrirModalDetalleFacturacion = useCallback(
    (fecha, filtro) => {
      const [anio, mes] = fecha.split("-");
      const titulo = generarTituloModal(filtro, mes, anio);
      setTituloModalEstadisticasFacturacion(titulo);

      const fechaFormateadaANumero = formatearFecha(fecha);

      const sucursalesActuales = sucursalesActualesRef.current;

      const body = crearBodySolicitud(
        user.rut,
        filtro,
        fechaFormateadaANumero,
        sucursalesActuales,
      );

      obtenerFacturasEmitidas(body);
      setAbrirModalEstadisticasDeFacturacion(true);
    },
    [obtenerFacturasEmitidas, user.rut],
  );

  const generarTituloModal = (filtro, mes, anio) =>
    `${filtro} ${mes} del ${anio}`;

  const formatearFecha = (fecha) => {
    const [anio, mes] = fecha.split("-");
    return convertirFechaAFormatoNumerico(`${anio}/${mes}`);
  };

  // Función para generar texto de sucursales
  const obtenerTextoSucursales = useCallback(() => {
    const sucursales = obtenerSucursalesParaFiltro();

    if (
      sucursales.length === 0 ||
      !sucursalesEmpleado ||
      sucursales.length === sucursalesEmpleado.length
    ) {
      return "todas las sucursales";
    }

    if (sucursales.length === 1) {
      const labelSucursal = obtenerLabelSucursal(
        sucursales[0],
        sucursalesEmpleado,
      );
      return `sucursal ${capitalizarPalabras(labelSucursal)}`;
    }

    if (sucursales.length <= 3) {
      const labelsSucursales = mapearSucursalesALabels(
        sucursales,
        sucursalesEmpleado,
      );
      return `sucursales ${capitalizarPalabras(labelsSucursales.join(", "))}`;
    }

    return `${sucursales.length} sucursales seleccionadas`;
  }, [obtenerSucursalesParaFiltro, sucursalesEmpleado]);

  // Títulos dinámicos
  const tituloFacturacionRecaudacion = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    const anio = fechaDesde(anioSeleccionado).year();
    return `Resumen anual ${anio} - ${textoSucursales}`;
  }, [obtenerTextoSucursales, anioSeleccionado]);

  const subheaderFacturacionRecaudacion = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    const anio = fechaDesde(anioSeleccionado).year();
    return `Facturación y recaudación del año ${anio} para ${textoSucursales}`;
  }, [obtenerTextoSucursales, anioSeleccionado]);

  const tituloEstadisticasFacturacion = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    const anio = fechaDesde(anioSeleccionado).year();
    return `Estadísticas de facturación ${anio} - ${textoSucursales}`;
  }, [obtenerTextoSucursales, anioSeleccionado]);

  const subheaderEstadisticasFacturacion = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    const anio = fechaDesde(anioSeleccionado).year();
    return `12 meses del año ${anio} para ${textoSucursales}`;
  }, [obtenerTextoSucursales, anioSeleccionado]);

  const tituloFacturacionSucursales = useMemo(() => {
    const sucursales = obtenerSucursalesParaFiltro();
    if (sucursales.length === 1) {
      const labelSucursal = obtenerLabelSucursal(
        sucursales[0],
        sucursalesEmpleado,
      );
      return `Facturación ${capitalizarPalabras(labelSucursal)}`;
    }
    return "Facturación por sucursales";
  }, [obtenerSucursalesParaFiltro, sucursalesEmpleado]);

  const tituloRecaudacionSucursales = useMemo(() => {
    const sucursales = obtenerSucursalesParaFiltro();
    if (sucursales.length === 1) {
      const labelSucursal = obtenerLabelSucursal(
        sucursales[0],
        sucursalesEmpleado,
      );
      return `Recaudación ${capitalizarPalabras(labelSucursal)}`;
    }
    return "Recaudación por sucursales";
  }, [obtenerSucursalesParaFiltro, sucursalesEmpleado]);

  const tituloCrecimientoClientes = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    return `Crecimiento de clientes - ${textoSucursales}`;
  }, [obtenerTextoSucursales]);

  const subheaderCrecimientoClientes = useMemo(() => {
    const textoSucursales = obtenerTextoSucursales();
    const anio = fechaDesde(anioSeleccionado).year();
    return `Tendencia de crecimiento mensual para ${textoSucursales} (año ${anio})`;
  }, [obtenerTextoSucursales, anioSeleccionado]);

  return (
    <>
      <Container maxWidth="xl">
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Typography variant="h4">Informes 📄</Typography>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              alignItems="center"
            >
              {/* Filtro de Sucursales */}
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <SucursalFilter
                  id="filtro-sucursales"
                  label="Filtrar por sucursales"
                  rutEmpleado={user.rut}
                  sucursalesSeleccionadas={sucursalesSeleccionadas}
                  onChangeSucursales={handleSetSucursalesSeleccionadas}
                />
              </Grid>

              {/* Filtro de Año */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <AnioFilter
                  id="filtro-anio"
                  label="Filtrar por año"
                  anioSeleccionado={anioSeleccionado}
                  onChangeAnio={handleSetAnioSeleccionado}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={12}>
            <AppWidgetsFacturacionRecaudacionPendientes
              title={tituloFacturacionRecaudacion}
              subheader={subheaderFacturacionRecaudacion}
              loading={cargandoDatosFacturacionAnio}
              chart={{
                series:
                  !cargandoDatosFacturacionAnio && datosFacturacionAnio
                    ? datosFacturacionAnio
                    : [],
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <AppGraficoDeBarras
              title={tituloEstadisticasFacturacion}
              subheader={subheaderEstadisticasFacturacion}
              loading={cargandoDatosEstadisticasFacturacion}
              manejarClickGrafico={abrirModalDetalleFacturacion}
              chart={{
                categories:
                  !cargandoDatosEstadisticasFacturacion &&
                  datosEstadisticasFacturacion
                    ? datosEstadisticasFacturacion?.labelChart
                    : [],
                series: {
                  data:
                    !cargandoDatosEstadisticasFacturacion &&
                    datosEstadisticasFacturacion
                      ? datosEstadisticasFacturacion?.dataChart
                      : [],
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <AppFacturasSucursales
              title={tituloFacturacionSucursales}
              subheader="Detalle del año en curso"
              loading={cargandoDatosFacturacionPorSucursales}
              chart={{
                series: !cargandoDatosFacturacionPorSucursales
                  ? datosFacturacionPorSucursales.facturasTotales
                  : [],
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <AppFacturasSucursales
              title={tituloRecaudacionSucursales}
              subheader="Detalle del año en curso"
              loading={cargandoDatosFacturacionPorSucursales}
              chart={{
                series: !cargandoDatosFacturacionPorSucursales
                  ? datosFacturacionPorSucursales.recaudacionesTotales
                  : [],
              }}
            />
          </Grid>

          {/* <Grid item xs={12} md={12} lg={6}>
        <AppCrecimientoClientes
          title="Crecimiento de clientes"
          subheader="Crecimiento respecto al mes anterior"
          chart={{
            labels: !cargandoDatosCrecimientoClientes
              ? datosCrecimientoClientes.crecimientoRelacionMesAnterior.map(
                  (dato) => dato.fecha
                )
              : [],
            series: [
              {
                name: "Nuevos clientes",
                type: "column",
                fill: "solid",
                data: !cargandoDatosCrecimientoClientes
                  ? datosCrecimientoClientes.crecimientoRelacionMesAnterior.map(
                      (dato) => dato.nuevosClientes
                    )
                  : [],
              },
              {
                name: "Tasa de crecimiento",
                type: "area",
                fill: "gradient",
                data: !cargandoDatosCrecimientoClientes
                  ? datosCrecimientoClientes.crecimientoRelacionMesAnterior.map(
                      (dato) => dato.tasaCrecimiento
                    )
                  : [],
              },
            ],
          }}
        />
      </Grid> */}
          <Grid item xs={12} md={12} lg={12}>
            <AppCrecimientoClientes
              title={tituloCrecimientoClientes}
              subheader={subheaderCrecimientoClientes}
              loading={cargandoDatosCrecimientoClientes}
              chart={{
                labels: !cargandoDatosCrecimientoClientes
                  ? datosCrecimientoClientes.crecimientoRelacionAcumuladoMesAnterior.map(
                      (dato) => dato.fecha,
                    )
                  : [],
                series: [
                  {
                    name: "Nuevos clientes",
                    type: "column",
                    fill: "solid",
                    data: !cargandoDatosCrecimientoClientes
                      ? datosCrecimientoClientes.crecimientoRelacionAcumuladoMesAnterior.map(
                          (dato) => dato.nuevosClientes,
                        )
                      : [],
                  },
                  {
                    name: "Tasa de crecimiento",
                    type: "area",
                    fill: "gradient",
                    data: !cargandoDatosCrecimientoClientes
                      ? datosCrecimientoClientes.crecimientoRelacionAcumuladoMesAnterior.map(
                          (dato) => dato.tasaCrecimiento,
                        )
                      : [],
                  },
                ],
              }}
            />
          </Grid>

          {/* <Grid item xs={12} md={12} lg={12}>
          <GraficoTasas
            getData={getTasaCrecimientoCliente}
            isSuccess={isSuccessTasaCrecimientoCliente}
            data={tasaCrecimientoClienteData}
            title={"Tasa de crecimiento de clientes"}
            simbolo={"%"}
          />
        </Grid> */}
        </Grid>
      </Container>
      {abrirModalEstadisticasDeFacturacion && (
        <ModalDetalleFacturas
          isOpen={abrirModalEstadisticasDeFacturacion}
          setOpen={setAbrirModalEstadisticasDeFacturacion}
          title={tituloModalEstadisticasFacturacion}
          cargandoDatos={obteniendoFacturasEmitidas}
          rows={
            !obteniendoFacturasEmitidas && facturasEmitidas
              ? facturasEmitidas
              : []
          }
          tipo="recaudadoFull"
        />
      )}
    </>
  );
}
